import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import Footer from "../../components/Footer";
import Header from "../../Header";
import Random from "../../assets/About-Landing.jpg";
import { HashLink } from "react-router-hash-link";

// Animation Variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8 } }
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  show: { opacity: 1, x: 0, transition: { duration: 0.8 } }
};

const About = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      q: "What services do you provide?",
      a: "We offer online counseling sessions, therapy consultations, emotional wellness coaching, mental health assessments, and more.",
    },
    {
      q: "How do online therapy sessions work?",
      a: "Simply choose a therapist, book a session, and join the secure video call link. No downloads required.",
    },
    {
      q: "Are my sessions private?",
      a: "Absolutely. All sessions follow strict confidentiality and privacy guidelines encrypted end-to-end.",
    },
    {
      q: "Can I reschedule anytime?",
      a: "Yes, you can reschedule your booking through your dashboard with prior notice.",
    },
  ];

  const team = [
    { name: "Sarah Johnson", role: "Lead Psychologist", img: "https://randomuser.me/api/portraits/women/44.jpg" },
    { name: "David Williams", role: "Clinical Therapist", img: "https://randomuser.me/api/portraits/men/32.jpg" },
    { name: "Alicia Brown", role: "Mental Wellness Coach", img: "https://randomuser.me/api/portraits/women/36.jpg" },
    { name: "Michael Chen", role: "Therapy Coordinator", img: "https://randomuser.me/api/portraits/men/48.jpg" },
  ];

  return (
    <div className="bg-white w-full font-inter">
      <Header />

      {/* ================= HERO ================= */}
      <motion.section
        className="relative bg-[#FFF0DC] py-24 px-6 md:px-12 text-center overflow-hidden"
        initial="hidden"
        animate="show"
      >
        {/* Floating background blobs */}
        <motion.div
          className="absolute -top-10 -left-10 w-60 h-60 bg-orange-200 opacity-25 rounded-full blur-3xl"
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute top-20 -right-10 w-72 h-72 bg-orange-300 opacity-20 rounded-full blur-3xl"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 7, repeat: Infinity }}
        />

        <motion.h1
          variants={fadeUp}
          className="text-4xl md:text-6xl font-extrabold text-gray-900 relative z-10"
        >
          About <span className="text-orange-500">Quirkle</span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          transition={{ delay: 0.2 }}
          className="text-gray-600 max-w-3xl mx-auto mt-4 text-lg"
        >
          Empowering millions to take the first step toward better mental health —
          anytime, anywhere.
        </motion.p>
      </motion.section>

      {/* ================= WHO WE ARE ================= */}
      <section id="weare" className="max-w-7xl mx-auto py-20 px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-16 items-center">

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeLeft}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Are</h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              At Quirkle, we believe mental health care should be accessible,
              convenient, and judgment-free. Our platform connects individuals with
              certified therapists through seamless online sessions.
            </p>
            <p className="text-gray-600 leading-relaxed mt-4 text-lg">
              We are driven by compassion, backed by science, and committed to
              creating a world where emotional well-being is a priority.
            </p>
          </motion.div>

          <motion.img
            variants={fadeRight}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            src={Random}
            className="rounded-3xl w-full h-[380px] object-cover shadow-xl"
            alt="team"
          />
        </div>
      </section>

      {/* ================= OUR VISION ================= */}
      <motion.section
        id="vision"
        className="bg-[#FFF5E8] py-20 px-6 md:px-12 text-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        variants={fadeUp}
      >
        <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
        <p className="text-gray-600 max-w-3xl mx-auto mt-4 text-lg leading-relaxed">
          To build a global mental wellness ecosystem where therapy is stigma-free,
          affordable, and accessible — tailored to every individual.
        </p>
      </motion.section>

      {/* ================= STORY TIMELINE ================= */}
      <section className="max-w-6xl mx-auto py-24 px-6 md:px-12">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-3xl font-bold text-gray-900 text-center mb-14"
        >
          Our Journey
        </motion.h2>

        <motion.div
          className="space-y-12 border-l-4 border-orange-400 pl-6"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: { staggerChildren: 0.25 }
            }
          }}
        >
          {[
            { year: "2021 — The Idea", text: "Quirkle began as a simple idea: therapy shouldn’t be complicated or scary." },
            { year: "2022 — Building the Team", text: "We united experts to create a user-friendly platform." },
            { year: "2023 — First 10k Users", text: "Our community grew faster than expected." },
            { year: "2024 — Expanding Globally", text: "Serving users across 18+ countries." }
          ].map((item, i) => (
            <motion.div key={i} variants={fadeUp}>
              <h3 className="text-xl font-semibold text-gray-900">{item.year}</h3>
              <p className="text-gray-600 mt-2">{item.text}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ================= TEAM ================= */}
      <section id="team" className="max-w-7xl mx-auto py-20 px-6 md:px-12">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-3xl font-bold text-center text-gray-900 mb-12"
        >
          Meet Our Team
        </motion.h2>

        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-10">
          {team.map((member, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 200 }}
              className="text-center bg-white shadow-lg rounded-2xl p-6 hover:shadow-xl transition-all"
            >
              <img
                src={member.img}
                className="rounded-full w-28 h-28 mx-auto mb-4 object-cover shadow-md"
                alt={member.name}
              />
              <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
              <p className="text-sm text-gray-500">{member.role}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= TERMS ================= */}
      <motion.section
        id="terms"
        className="bg-[#FFF0DC] py-20 px-6 md:px-12"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Terms & Conditions</h2>
          <ul className="text-gray-600 leading-relaxed space-y-4 text-lg">
            <li>• Sessions are confidential and protected.</li>
            <li>• Respectful communication is mandatory.</li>
            <li>• Rescheduling is allowed with prior notice.</li>
            <li>• Users must be 18+ or have guardian consent.</li>
            <li>• Harassment or abusive behavior is prohibited.</li>
          </ul>
        </div>
      </motion.section>

      {/* ================= FAQ ================= */}
      <section id="faq" className="max-w-7xl mx-auto py-20 px-6 md:px-12">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-3xl font-bold text-center text-gray-900 mb-8"
        >
          FAQs
        </motion.h2>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ show: { transition: { staggerChildren: 0.2 } } }}
          className="space-y-4"
        >
          {faqs.map((item, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white shadow-lg rounded-xl p-6">
              <button
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                className="w-full flex justify-between items-center"
              >
                <span className="font-medium text-gray-900">{item.q}</span>
                {openFAQ === i ? (
                  <ChevronUp className="text-orange-500" />
                ) : (
                  <ChevronDown className="text-orange-500" />
                )}
              </button>

              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openFAQ === i ? "max-h-40 mt-3" : "max-h-0"
                }`}
              >
                <p className="text-gray-600 text-sm">{item.a}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
